
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Types
export interface EmailAttachment {
  id: number;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  template_id: number;
  uploaded_at?: string;
}

export interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  content: string;
  description?: string;
  category: 'notification' | 'marketing' | 'transactional' | 'custom';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: number;
  attachments?: EmailAttachment[];
  creator?: {
    id: number;
    name: string;
    firstname: string;
    email: string;
  };
}

export interface EmailTemplateFormData {
  name: string;
  subject: string;
  content: string;
  description?: string;
  category?: string;
  attachments?: File[];
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Récupérer tous les templates avec pagination
 */
export const getAllTemplates = async (
  page: number = 1,
  limit: number = 10
): Promise<PaginatedResponse<EmailTemplate>> => {
  try {
    const token = localStorage.getItem("token");
    
    const response = await axios.get(`${API_URL}/email-template`, {
      params: { page, limit },
      headers: token ? { 'Authorization': `${token}` } : {}
    });

    return response.data;
  } catch (error: any) {
    console.error('Erreur getAllTemplates:', error);
    
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    }
    throw new Error('Une erreur est survenue lors de la récupération des templates.');
  }
};

/**
 * Récupérer un template par son ID
 */
export const getTemplateById = async (id: number): Promise<ApiResponse<EmailTemplate>> => {
  try {
    const token = localStorage.getItem("token");
    
    const response = await axios.get(`${API_URL}/email-template/${id}`, {
      headers: token ? { 'Authorization': `${token}` } : {}
    });

    return response.data;
  } catch (error: any) {
    console.error('Erreur getTemplateById:', error);
    
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    }
    throw new Error('Une erreur est survenue lors de la récupération du template.');
  }
};

/**
 * Créer un nouveau template avec pièces jointes
 */
export const createTemplate = async (formData: FormData): Promise<ApiResponse<EmailTemplate>> => {
  try {
    const token = localStorage.getItem("token");
    
    console.log('📤 Création template avec données:', {
      name: formData.get('name'),
      subject: formData.get('subject'),
      hasAttachments: formData.has('attachments')
    });

    const response = await axios.post(`${API_URL}/email-template`, formData, {
      headers: {
        ...(token && { 'Authorization': `${token}` }),
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('Erreur createTemplate:', error);
    
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    }
    throw new Error('Une erreur est survenue lors de la création du template.');
  }
};

/**
 * Mettre à jour un template existant
 */
export const updateTemplate = async (
  id: number, 
  formData: FormData
): Promise<ApiResponse<EmailTemplate>> => {
  try {
    const token = localStorage.getItem("token");
    
    console.log('📝 Mise à jour template:', id);

    const response = await axios.put(`${API_URL}/email-template/${id}`, formData, {
      headers: {
        ...(token && { 'Authorization': `${token}` }),
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('Erreur updateTemplate:', error);
    
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    }
    throw new Error('Une erreur est survenue lors de la mise à jour du template.');
  }
};

/**
 * Supprimer un template
 */
export const deleteTemplate = async (id: number): Promise<ApiResponse<null>> => {
  try {
    const token = localStorage.getItem("token");
    
    const response = await axios.delete(`${API_URL}/email-template/${id}`, {
      headers: token ? { 'Authorization': `${token}` } : {}
    });

    return response.data;
  } catch (error: any) {
    console.error('Erreur deleteTemplate:', error);
    
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    }
    throw new Error('Une erreur est survenue lors de la suppression du template.');
  }
};

/**
 * Supprimer une pièce jointe spécifique
 */
export const deleteAttachment = async (attachmentId: number): Promise<ApiResponse<null>> => {
  try {
    const token = localStorage.getItem("token");
    
    const response = await axios.delete(`${API_URL}/email-template/attachments/${attachmentId}`, {
      headers: token ? { 'Authorization': `${token}` } : {}
    });

    return response.data;
  } catch (error: any) {
    console.error('Erreur deleteAttachment:', error);
    
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || error.message);
    }
    throw new Error('Une erreur est survenue lors de la suppression de la pièce jointe.');
  }
};

/**
 * Fonction utilitaire pour préparer le FormData pour la création/édition
 */
export const prepareTemplateFormData = (data: EmailTemplateFormData): FormData => {
  const formData = new FormData();
  
  formData.append('name', data.name);
  formData.append('subject', data.subject);
  formData.append('content', data.content);
  
  if (data.description) {
    formData.append('description', data.description);
  }
  
  if (data.category) {
    formData.append('category', data.category);
  }
  
  if (data.attachments && data.attachments.length > 0) {
    data.attachments.forEach((file) => {
      formData.append('attachments', file);
    });
  }
  
  return formData;
};

const API_URL_BACK = process.env.REACT_APP_API_BASE;

export const getFileUrl = (filePath: string): string => {
  if (!filePath) return '';
  const fileName = filePath.split('\\').pop()?.split('/').pop() || '';
  return `${API_URL_BACK}/storage/uploads/email-attachments/${fileName}`;
};